import { camelize, snakeize } from 'casing'
import { AxiosError, AxiosResponse } from 'axios'
import { get, isEmpty, snakeCase } from 'lodash'
import {
  fetchFlyoutAccountDetails,
  fetchOrganizationAccounts,
  getUrlFromCloudAccount,
  sendAzureData,
  sendAzurePartnerData,
} from '../../api/cloudAccounts'
import { startLoading, stopLoading } from '../../modules/loading/actions'
import { LoadingTypes } from '../../modules/loading/reducer'
import { isLoading } from '../../utils/loadingState'
import { actionTypeWrapper } from '../../utils/actionTypeWrapper'
import { errorLogger } from '../../utils/errorLogger'
import { getErrorMessages } from '../../utils/error'
import { RequestFailureMessage } from '../../utils/messagesContants'
import { updateAppAlert } from '../../modules/appAlert/actions'
import { setItem } from '../../utils/localStorage'
import { isAdminRole } from '../../utils/isAdminRole'
import { PartnerType } from '../../types/partner'
import type { ProductType } from '../../types'
import {
  clearAzureOauthAccountId,
  clearAzureOauthCache,
  getAzureOauthCacheKey,
} from './azureOauth'
import {
  deactivateAzureAccount,
  fetchAzureAccountDetails,
  fetchAzureSolutionsData,
} from './azureAccountApi'

export const awsTemplateNameCMCM = 'marketplace_listing'

const SET_ACCOUNTS = 'SET_ACCOUNTS'
const SET_ACCOUNT_ONBOARDING_DATA = 'SET_ACCOUNT_ONBOARDING_DATA'
const UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE =
  'UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE'
const SET_AZURE_ACCOUNT_DETAILS = 'SET_AZURE_ACCOUNT_DETAILS'
const SET_SELECTED_AZURE_ACCOUNT_DETAILS = 'SET_SELECTED_AZURE_ACCOUNT_DETAILS'
const CLEAR_SELECTED_ACCOUNT_DATA = 'CLEAR_SELECTED_ACCOUNT_DATA'

const oppSyncProductPrd = 'oppsync_prd'
const azureScopesUrls = [
  'https://api.partner.microsoft.com/user_impersonation',
  'https://api.partnercenter.microsoft.com/user_impersonation',
]

const cloudTypeToApiValue = (cloudType: string) =>
  cloudType === 'AZURE' ? 'AZ' : cloudType

type Dispatch = (action: unknown) => Promise<unknown>
type GetState = () => any

type RegistrationAccount = {
  accountDetails?: {
    name?: string
    externalAccountId?: string
    roles: {
      rows: Array<{ awsTemplateId?: string; iamRoleArn?: string }>
    }
  }
}

type AzureConfigMetaDataResponse = {
  steps: {
    prerequisites: boolean
    mpc_account_details: boolean
    microsoft_product_details: boolean
    link_mpc_account: boolean
  }
  is_azure_onboarding_completed: boolean
}

const setAccounts = (accounts: unknown) => ({
  type: SET_ACCOUNTS,
  payload: accounts,
})

export const fetchAccounts =
  (templateName: string, cloudType: string, productId: ProductType) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const stateBefore = getState()
    const isAdmin = isAdminRole(stateBefore.userProfile.userProfile)
    const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
    const organizationId =
      stateBefore.AccountData?.[partnerType]?.partnerData?.partnerId || ''
    const selectedPartnerIdForAdmin =
      stateBefore.AccountData?.admin?.selectedPartnerId?.trim() || ''
    const effectiveOrganizationId = isAdmin
      ? selectedPartnerIdForAdmin || organizationId
      : organizationId

    if (!effectiveOrganizationId) {
      return
    }

    dispatch(startLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    try {
      const response = await fetchOrganizationAccounts(
        effectiveOrganizationId,
        cloudType,
        productId
      )
      await dispatch(
        actionTypeWrapper(productId, setAccounts(camelize(response.data.rows)))
      )
    } catch (e) {
      const globalState = getState()
      errorLogger({ globalState })(e as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    }
  }

const getInputsFields = ({
  productType,
  accountFromAllAccounts,
  id,
  flyoutSellerinfo,
}: {
  productType: ProductType
  accountFromAllAccounts?: RegistrationAccount
  id: string
  flyoutSellerinfo?: unknown
}) => {
  switch (productType) {
    case 'oppsync': {
      return [
        {
          keyName: 'iamRoleArn',
          value:
            accountFromAllAccounts?.accountDetails?.roles.rows.find(
              r => r.awsTemplateId === id
            )?.iamRoleArn || '',
          error: '',
          label: 'Enter Your ARN',
          showTestStatusButton: true,
          isRequired: true,
        },
      ]
    }
    case 'flyout': {
      return [
        {
          keyName: 'awsRoleArn',
          value: get(flyoutSellerinfo, 'sellerMetadata.awsRoleArn', ''),
          error: '',
          label: 'Enter Your ARN',
          showTestStatusButton: false,
          isRequired: true,
        },
        {
          keyName: 'awsCasS3BucketName',
          value: get(flyoutSellerinfo, 'sellerMetadata.awsCasS3BucketName', ''),
          error: '',
          label: 'Enter Your S3 CAS Bucket Name',
          showTestStatusButton: false,
          isRequired: true,
        },
        {
          keyName: 'awsCasSnsTopicArn',
          value: get(flyoutSellerinfo, 'sellerMetadata.awsCasSnsTopicArn', ''),
          error: '',
          label: 'Enter Your CAS SNS Topic ARN',
          showTestStatusButton: false,
          isRequired: true,
        },
      ]
    }
    case 'cloudfaas': {
      return []
    }
    default: {
      return []
    }
  }
}

const getSpecificFieldsBasedOnProductType = ({
  partnerData,
  productType,
  flyoutSellerinfo,
}: {
  partnerData: any
  productType: ProductType
  flyoutSellerinfo?: { origin?: string }
}) => {
  switch (productType) {
    case 'oppsync': {
      return {
        industries: snakeCase(partnerData?.industry || ''),
        partnershipTier: partnerData?.partnershipTier || '',
        partnershipType: partnerData?.partnershipType || '',
        terms: partnerData?.acceptedTermsAndConditions || false,
        isIsv: partnerData?.isIsv || false,
        isSrrp: partnerData?.isSrrp || false,
        accelerateProgram: partnerData?.accelerateProgram || false,
      }
    }
    case 'flyout': {
      return {
        isIsv: partnerData?.isIsv,
        origin: flyoutSellerinfo?.origin || '',
      }
    }
    case 'cloudfaas': {
      return {}
    }
    default: {
      return {}
    }
  }
}

const setAccountRegistrationData = (data: any) => {
  const {
    cloudType,
    organizationId,
    rows,
    productId,
    name,
    externalId,
    industries,
    partnershipTier,
    partnershipType,
    terms,
    isIsv,
    isSrrp,
    accelerateProgram,
    origin,
  } = data
  return {
    type: SET_ACCOUNT_ONBOARDING_DATA,
    payload: {
      cloudType,
      organizationId,
      roles: rows,
      productId,
      name,
      externalId,
      industries,
      partnershipTier,
      partnershipTypes: partnershipType,
      terms,
      isIsv,
      isSrrp,
      accelerateProgram,
      origin,
    },
  }
}

export const getAccountRegistrationData =
  (
    cloudType: string,
    name: string,
    productId: string,
    partnerId: string,
    productType: ProductType,
    partnerType: PartnerType,
    accountFromAllAccounts?: RegistrationAccount,
    flyoutSellerinfo?: { origin?: string }
  ) =>
  async (dispatch: Dispatch, getState: GetState) => {
    await dispatch(startLoading(LoadingTypes.TEMPLATES))
    const partnerData = getState().AccountData[partnerType].partnerData
    try {
      const { data } = await fetchFlyoutAccountDetails(
        name,
        cloudType,
        productId,
        partnerId
      )
      if (!isEmpty(data.rows)) {
        data.rows.forEach((row: any) => {
          row.inputsFields = getInputsFields({
            productType,
            accountFromAllAccounts,
            id: row.id,
            flyoutSellerinfo,
          })
        })
      }

      const newData = {
        ...camelize(data),
        organizationId: partnerId,
        productId: productId,
        name: accountFromAllAccounts?.accountDetails?.name || '',
        externalId:
          accountFromAllAccounts?.accountDetails?.externalAccountId || '',
        ...getSpecificFieldsBasedOnProductType({
          partnerData,
          productType,
          flyoutSellerinfo,
        }),
      }
      await dispatch(
        actionTypeWrapper(productType, setAccountRegistrationData(newData))
      )
    } catch (error) {
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      await dispatch(stopLoading(LoadingTypes.TEMPLATES))
    }
  }

export const updateValueOnAccountDetails = (key: string, value: unknown) => ({
  type: UPDATE_VALUE_ON_ACCOUNT_DETAILS_AZURE,
  payload: {
    key,
    value,
  },
})

const setAzureAccountDetails = (accountDetails: unknown) => ({
  type: SET_AZURE_ACCOUNT_DETAILS,
  payload: accountDetails,
})

const setSelectedAzureAccountDetails = (
  accountId: string,
  accountDetails: unknown
) => ({
  type: SET_SELECTED_AZURE_ACCOUNT_DETAILS,
  payload: {
    accountId,
    accountDetails,
  },
})

const clearSelectedAccountData = (cloudType?: string) => ({
  type: CLEAR_SELECTED_ACCOUNT_DATA,
  payload: cloudType ? { cloudType } : undefined,
})

const deriveAzureConfigMetadata = ({
  existingMetadata,
  isConnectionActive,
}: {
  existingMetadata?: AzureConfigMetaDataResponse
  isConnectionActive: boolean
}): AzureConfigMetaDataResponse => {
  const existingSteps = existingMetadata?.steps ?? {
    prerequisites: false,
    mpc_account_details: false,
    microsoft_product_details: false,
    link_mpc_account: false,
  }

  const steps = {
    prerequisites: existingSteps.prerequisites,
    mpc_account_details: existingSteps.mpc_account_details,
    microsoft_product_details: existingSteps.microsoft_product_details,
    link_mpc_account: isConnectionActive,
  }

  const is_azure_onboarding_completed = Boolean(
    isConnectionActive &&
      steps.prerequisites &&
      steps.mpc_account_details &&
      steps.microsoft_product_details
  )

  return {
    steps,
    is_azure_onboarding_completed,
  }
}

const inFlightAzureAccountDetailsRequests = new Map<string, Promise<void>>()

const getAzureAccountDetailsRequestKey = ({
  partnerId,
  product,
  isSettingsPage,
}: {
  partnerId: string
  product: ProductType
  isSettingsPage: boolean
}) => `${product}:${isSettingsPage ? 'settings' : 'onboarding'}:${partnerId}`

export const initiateAzureOAuthFlow =
  (productType: ProductType, partnerId: string, isSettingsPage = false) =>
  async (dispatch: Dispatch, getState: GetState) => {
    await dispatch(startLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    const state = getState()
    const partnerData = state.AccountData.user.partnerData
    try {
      clearAzureOauthCache(partnerId)

      const { data } = await getUrlFromCloudAccount(partnerId)
      const oauthPayload = {
        ...data,
        cloudType: partnerData?.activeCloudProvider,
        organizationId: partnerId,
        productId: productType.toUpperCase(),
      }
      await setItem(getAzureOauthCacheKey(partnerId), oauthPayload, {
        ttlInSeconds: 60 * 10,
      })

      const camelized = camelize(oauthPayload) as {
        authUri?: string
        adminConsentUri?: string
      }

      if (isSettingsPage) {
        const existing =
          getState().accountForm[productType].selectedAccountDetails.AZURE
        await dispatch(
          actionTypeWrapper(
            productType,
            setSelectedAzureAccountDetails(partnerId, {
              ...existing,
              ...camelized,
            })
          )
        )
      } else {
        await dispatch(
          actionTypeWrapper(productType, setAzureAccountDetails(camelized))
        )
      }

      return {
        authUri: camelized.authUri || null,
        adminConsentUri: camelized.adminConsentUri || null,
      }
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      return null
    } finally {
      await dispatch(stopLoading(LoadingTypes.CMCM_FETCHING_ACCOUNTS))
    }
  }

export const sendAzureAccountRegistration =
  (
    productType: ProductType,
    code: string,
    stateFromUrl: string,
    sessionState: string,
    partnerId: string
  ) =>
  async (dispatch: Dispatch, getState: GetState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    const state = getState()

    const selectedAccountId = state.accountForm[productType].selectedAccountId
    const isSelectedAccount = selectedAccountId === partnerId

    const azureAccountDetails = isSelectedAccount
      ? state.accountForm[productType].selectedAccountDetails.AZURE
      : state.accountForm[productType].accountDetails.AZURE

    const dataToSend = {
      cloudType: cloudTypeToApiValue('AZURE'),
      productId: productType.toUpperCase(),
      name: oppSyncProductPrd,
      scopes: azureScopesUrls,
      accountDetails: {
        code,
        state: stateFromUrl,
        sessionState,
      },
    }
    const partnerDataToSend = {
      mpnId: azureAccountDetails.MPNID,
      companyName: azureAccountDetails.name,
      industry: azureAccountDetails.industries,
      partnerType: azureAccountDetails.microsoftPartnerType,
      isIsv: azureAccountDetails.ISV,
    }

    try {
      await sendAzureData(snakeize(dataToSend), partnerId)
      await sendAzurePartnerData(snakeize(partnerDataToSend), partnerId)

      clearAzureOauthCache(partnerId)
      clearAzureOauthAccountId()

      await dispatch(
        updateAppAlert({
          message: 'Connection Established Successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      return { alreadyConnected: false as const }
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse<{
        code?: string
        track_id?: string
      }>
      if (
        errorResponse?.status === 409 &&
        errorResponse.data?.code === 'ACCOUNT_ID_EXISTS'
      ) {
        clearAzureOauthCache(partnerId)
        clearAzureOauthAccountId()
        try {
          await sendAzurePartnerData(snakeize(partnerDataToSend), partnerId)
        } catch {
          // Partner metadata sync is best-effort when auth already exists
        }
        await dispatch(
          updateAppAlert({
            message: 'Microsoft Partner Center is already connected',
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
        return { alreadyConnected: true as const }
      }
      if (
        errorResponse?.status === 403 &&
        errorResponse?.data?.code === 'ADMIN_CONSENT_REQUIRED'
      ) {
        await dispatch(
          updateAppAlert({
            message:
              'A Microsoft admin must approve Labra for your organization, then try Connect again.',
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        const globalState = getState()
        errorLogger({ globalState })(error as Error)
        return { alreadyConnected: false as const, error: true as const }
      }
      await dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            (error as AxiosError).response as AxiosResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(error as Error)
      return { alreadyConnected: false as const, error: true as const }
    } finally {
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const getAzureAccountDetails =
  (partnerId: string, product: ProductType, isSettingsPage = false) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const requestKey = getAzureAccountDetailsRequestKey({
      partnerId,
      product,
      isSettingsPage,
    })
    const existingRequest = inFlightAzureAccountDetailsRequests.get(requestKey)
    if (existingRequest) {
      await existingRequest
      return
    }

    if (isSettingsPage) {
      const selectedAccountId =
        getState().accountForm[product].selectedAccountId
      const selectedAccountDetails =
        getState().accountForm[product].selectedAccountDetails.AZURE

      if (
        selectedAccountId === partnerId &&
        selectedAccountDetails?.fetchedForPartner === partnerId
      ) {
        if (
          isLoading(getState().loading)(
            LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS
          )
        ) {
          return
        }
        return
      }

      if (selectedAccountId && selectedAccountId !== partnerId) {
        await dispatch(clearSelectedAccountData('AZURE'))
      }
    } else {
      const currentAccountDetails =
        getState().accountForm[product].accountDetails.AZURE
      if (currentAccountDetails?.fetchedForPartner === partnerId) {
        if (
          isLoading(getState().loading)(
            LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS
          )
        ) {
          return
        }
        return
      }
    }

    const requestPromise = (async () => {
      dispatch(startLoading(LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS))
      try {
        const azureAccountDetails = await fetchAzureAccountDetails(partnerId)
        const solutions = await fetchAzureSolutionsData(partnerId)

        const accountStatus = azureAccountDetails.status || 'draft'
        const isConnectionActive = Boolean(
          accountStatus === 'active' &&
            azureAccountDetails.MPNID &&
            azureAccountDetails.microsoftPartnerType?.length > 0
        )

        const azureConfigMetadata = deriveAzureConfigMetadata({
          existingMetadata: azureAccountDetails.azureConfigMetadata,
          isConnectionActive,
        })

        const accountDetailsPayload = {
          ...azureAccountDetails,
          id: azureAccountDetails.id || partnerId,
          status: accountStatus,
          organizationId: partnerId,
          fetchedForPartner: partnerId,
          terms: true,
          grantConsent: true,
          azureSolutionsData: solutions,
          azureConfigMetadata,
        }

        if (isSettingsPage) {
          await dispatch(
            actionTypeWrapper(
              product,
              setSelectedAzureAccountDetails(partnerId, accountDetailsPayload)
            )
          )
        } else {
          dispatch(
            actionTypeWrapper(
              product,
              setAzureAccountDetails(accountDetailsPayload)
            )
          )
        }
      } catch (error) {
        const e = error as AxiosError
        const errorResponse = e.response as AxiosResponse
        if (errorResponse && errorResponse.status === 404) {
          if (!isSettingsPage) {
            dispatch(
              actionTypeWrapper(
                product,
                setAzureAccountDetails({
                  fetchedForPartner: partnerId,
                  azureConfigMetadata: {
                    steps: {
                      prerequisites: false,
                      mpc_account_details: false,
                      microsoft_product_details: false,
                      link_mpc_account: false,
                    },
                    is_azure_onboarding_completed: false,
                  },
                })
              )
            )
          }
          return
        }
        if (!isSettingsPage) {
          dispatch(
            actionTypeWrapper(
              product,
              setAzureAccountDetails({
                fetchedForPartner: partnerId,
                azureConfigMetadata: {
                  steps: {
                    prerequisites: false,
                    mpc_account_details: false,
                    microsoft_product_details: false,
                    link_mpc_account: false,
                  },
                  is_azure_onboarding_completed: false,
                },
              })
            )
          )
        }
        errorLogger({ globalState: getState() })(error as Error)
      } finally {
        dispatch(stopLoading(LoadingTypes.AZURE_FETCHING_ACCOUNT_DETAILS))
      }
    })()

    inFlightAzureAccountDetailsRequests.set(requestKey, requestPromise)

    try {
      await requestPromise
    } finally {
      if (
        inFlightAzureAccountDetailsRequests.get(requestKey) === requestPromise
      ) {
        inFlightAzureAccountDetailsRequests.delete(requestKey)
      }
    }
  }

export const disconnectAzureAccount =
  (
    partnerId: string,
    product: ProductType,
    accountId: string,
    isSettingsPage = false
  ) =>
  async (dispatch: Dispatch, getState: GetState) => {
    dispatch(startLoading(LoadingTypes.AZURE_DISCONNECTING_ACCOUNT))
    const state = getState()

    const azureAccountDetails = isSettingsPage
      ? state.accountForm[product].selectedAccountDetails.AZURE
      : state.accountForm[product].accountDetails.AZURE

    try {
      const payload = { productId: product }
      await deactivateAzureAccount(partnerId, accountId, snakeize(payload))

      const updatedAccountDetails = {
        ...azureAccountDetails,
        status: 'deactivated' as const,
      }

      if (isSettingsPage) {
        await dispatch(
          actionTypeWrapper(
            product,
            setSelectedAzureAccountDetails(partnerId, updatedAccountDetails)
          )
        )
      } else {
        await dispatch(
          actionTypeWrapper(
            product,
            setAzureAccountDetails(updatedAccountDetails)
          )
        )
      }

      await dispatch(
        updateAppAlert({
          message: 'Disconnected account successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error) {
      const e = error as AxiosError
      const errorResponse = e.response as AxiosResponse
      if (errorResponse && errorResponse.status === 404) {
        return
      }
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: false,
        })
      )
      errorLogger({ globalState: getState() })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.AZURE_DISCONNECTING_ACCOUNT))
    }
  }
